import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";

export const Route = createFileRoute("/(public)/terms")({
  staticData: { title: "Terms of Service" },
  component: TermsPage,
});

function TermsPage() {
  return (
    <div className="relative min-h-screen bg-surface-950 font-sans selection:bg-brand-500/30 selection:text-brand-200">
      <div>
        <div className="mx-auto max-w-4xl px-6 pt-28">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Header */}
            <h1 className="text-4xl md:text-5xl font-display font-black text-white mb-3">
              Terms of Service
            </h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6">
              <p className="text-surface-400 text-sm">
                Last updated: February 1, 2026
              </p>
              <span className="text-surface-700 hidden sm:inline">|</span>
              <Link
                to="/privacy"
                className="text-sm text-brand-400 hover:text-brand-300 transition-colors inline-flex items-center gap-1"
              >
                Privacy Policy <ChevronRight className="size-3" />
              </Link>
            </div>

            {/* Content */}
            <div className="space-y-4">
              <Section id="section-1" title="1. Acceptance of Terms">
                <p>
                  By accessing and using Rivaled ("the Service"), you accept and
                  agree to be bound by the terms and provisions of this
                  agreement. If you do not agree to these Terms of Service,
                  please do not use the Service.
                </p>
              </Section>

              <Section id="section-2" title="2. Description of Service">
                <p>
                  Rivaled is a competitive gaming platform that provides
                  features and tools to facilitate organized competitive play,
                  including:
                </p>
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>Matchmaking and queue systems</li>
                  <li>Ranking and ELO rating systems</li>
                  <li>Club creation and management</li>
                  <li>Match history and performance statistics</li>
                  <li>Real-time communication between players</li>
                </ul>
              </Section>

              <Section id="section-3" title="3. User Accounts">
                <p>
                  To access certain features of the Service, you must create an
                  account. You are responsible for:
                </p>
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>
                    Maintaining the confidentiality of your account credentials
                  </li>
                  <li>All activities that occur under your account</li>
                  <li>
                    Ensuring your account information is accurate and up-to-date
                  </li>
                  <li>
                    Notifying us immediately of any unauthorized use of your
                    account
                  </li>
                </ul>
                <p>
                  You must be at least 13 years old to create an account. Users
                  under 18 should have parental or guardian consent.
                </p>
              </Section>

              <Section id="section-4" title="4. User Conduct">
                <p>You agree not to use the Service to:</p>
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>Violate any applicable laws or regulations</li>
                  <li>Harass, abuse, or harm other users</li>
                  <li>Impersonate others or misrepresent your affiliation</li>
                  <li>Transmit spam, malware, or other harmful code</li>
                  <li>
                    Attempt to gain unauthorized access to the Service or other
                    users' accounts
                  </li>
                  <li>
                    Cheat, exploit bugs, or use automation tools to gain unfair
                    advantages
                  </li>
                  <li>Share, sell, or transfer your account to others</li>
                  <li>Interfere with or disrupt the Service or servers</li>
                </ul>
              </Section>

              <Section
                id="section-5"
                title="5. Content and Intellectual Property"
              >
                <p>
                  All content, features, and functionality of the Service —
                  including text, graphics, logos, and software — are owned by
                  Rivaled and protected by copyright, trademark, and other
                  intellectual property laws.
                </p>
                <p>By submitting content to the Service:</p>
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>You retain ownership of any content you submit</li>
                  <li>
                    You grant Rivaled a worldwide, non-exclusive, royalty-free
                    license to use, display, and distribute your content in
                    connection with the Service
                  </li>
                  <li>
                    You represent that you have the right to submit such content
                  </li>
                </ul>
              </Section>

              <Section id="section-6" title="6. Subscriptions and Payments">
                <p>
                  Certain features of the Service may require a paid
                  subscription. By purchasing a subscription:
                </p>
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>
                    You authorize us to charge your payment method for the
                    subscription fee
                  </li>
                  <li>
                    Subscriptions automatically renew unless canceled before the
                    renewal date
                  </li>
                  <li>All sales are final — we do not offer refunds</li>
                  <li>
                    We reserve the right to change subscription pricing at any
                    time
                  </li>
                </ul>
                <p>
                  For billing questions, contact us at{" "}
                  <a
                    href="mailto:support@rivaled.gg"
                    className="text-brand-400 hover:text-brand-300 transition-colors"
                  >
                    support@rivaled.gg
                  </a>
                  .
                </p>
              </Section>

              <Section id="section-7" title="7. Termination">
                <p>
                  We reserve the right to suspend or terminate your account and
                  access to the Service at our discretion, without notice, for
                  conduct that we believe:
                </p>
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>Violates these Terms of Service</li>
                  <li>Is harmful to other users or the Service</li>
                  <li>Exposes us or other users to liability</li>
                </ul>
                <p>
                  You may terminate your account at any time through your
                  account settings or by contacting{" "}
                  <a
                    href="mailto:support@rivaled.gg"
                    className="text-brand-400 hover:text-brand-300 transition-colors"
                  >
                    support@rivaled.gg
                  </a>
                  .
                </p>
              </Section>

              <Section
                id="section-8"
                title="8. Disclaimers and Limitation of Liability"
              >
                <p>
                  The Service is provided "as is" without warranties of any
                  kind, either express or implied. We do not guarantee that the
                  Service will be uninterrupted, secure, or error-free.
                </p>
                <p>
                  To the fullest extent permitted by law, Rivaled shall not be
                  liable for:
                </p>
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>
                    Any indirect, incidental, special, consequential, or
                    punitive damages
                  </li>
                  <li>
                    Any loss of profits or revenues, whether incurred directly
                    or indirectly
                  </li>
                  <li>
                    Any loss of data, use, goodwill, or other intangible losses
                  </li>
                </ul>
              </Section>

              <Section id="section-9" title="9. Indemnification">
                <p>
                  You agree to indemnify and hold harmless Rivaled, its
                  officers, directors, employees, and agents from any:
                </p>
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>Claims, damages, or losses</li>
                  <li>Liabilities and expenses (including legal fees)</li>
                </ul>
                <p>
                  arising out of your use of the Service or violation of these
                  Terms.
                </p>
              </Section>

              <Section id="section-10" title="10. Changes to Terms">
                <p>
                  We reserve the right to modify these Terms of Service at any
                  time. We will provide notice of material changes by posting
                  the updated terms on the Service. Your continued use of the
                  Service after such changes constitutes acceptance of the new
                  terms.
                </p>
              </Section>

              <Section id="section-11" title="11. Governing Law">
                <p>
                  These Terms shall be governed by and construed in accordance
                  with the laws of the United States, without regard to its
                  conflict of law provisions.
                </p>
              </Section>

              <Section id="section-12" title="12. Contact">
                <p>
                  If you have any questions about these Terms of Service, please
                  contact us:
                </p>
                <ul className="list-disc list-outside ml-6 space-y-2">
                  <li>
                    Email:{" "}
                    <a
                      href="mailto:support@rivaled.gg"
                      className="text-brand-400 hover:text-brand-300 transition-colors"
                    >
                      support@rivaled.gg
                    </a>
                  </li>
                  <li>
                    Discord:{" "}
                    <a
                      href="https://discord.gg/gxwdZKRZ"
                      className="text-brand-400 hover:text-brand-300 transition-colors"
                    >
                      discord.gg/gxwdZKRZ
                    </a>
                  </li>
                </ul>
              </Section>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      id={id}
      className="scroll-mt-28 rounded-xl bg-surface-800/30 border border-surface-700/30 p-4"
    >
      <h2 className="text-xl font-display font-bold text-white mb-3">
        {title}
      </h2>
      <div className="text-surface-300 space-y-3 leading-relaxed text-[15px]">
        {children}
      </div>
    </section>
  );
}
